import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Application } from "@/contexts/ApplicationContext";
import { startApplication } from "@/services/cloudFunctionService";
import supabase from "@/supabase";
import * as Sentry from "@sentry/react";

// Local storage key for current application ID
const CURRENT_APPLICATION_ID_KEY = "currentApplicationId";

// Type for application update data
type ApplicationUpdateData = {
  applicationId: string;
  data: Partial<Application>;
};

/**
 * Hook for fetching application data by applicationId
 */
export const useFetchApplicationData = (applicationId: string | undefined) => {
  return useQuery({
    queryKey: ["application", applicationId],
    queryFn: async (): Promise<{
      success: boolean;
      message?: string;
      application?: Application;
    }> => {
      if (!applicationId) {
        return {
          success: false,
          message: "Application ID is required",
        };
      }

      try {
        // First, get the application record
        const { data: applicationData, error: applicationError } =
          await supabase
            .from("applications")
            .select("*")
            .eq("application_id", applicationId)
            .maybeSingle();
        if (applicationError) {
          console.error("Error fetching application:", applicationError);
          Sentry.captureException(applicationError, {
            extra: {
              context: "useFetchApplicationData",
              applicationId,
            },
          });
          return {
            success: false,
            message: "Application not found. Please check your application ID.",
          };
        }

        if (!applicationData) {
          return {
            success: true,
            message: "Application not found. Please check your application ID.",
          };
        }

        const [
          { data: applicantData, error: applicantError },
          { data: documentsData, error: documentsError },
          { data: assessmentData, error: assessmentError },
          { data: sessionBookings, error: bookingsError },
        ] = await Promise.all([
          supabase
            .from("applicants")
            .select("*")
            .eq("id", applicationData.applicant_id)
            .single(),
          supabase
            .from("documents")
            .select("*")
            .eq("application_id", applicationData.id)
            .neq("status", "Rejected"),
          supabase
            .from("assessments")
            .select("*")
            .eq("application_id", applicationData.id),
          supabase
            .from("session_bookings")
            .select("*, session:sessions(*, depot:depots(*))")
            .eq("application_id", applicationData.id),
        ]);

        if (applicantError || !applicantData) {
          console.error("Error fetching applicant:", applicantError);
          Sentry.captureException(applicantError, {
            extra: {
              context: "useFetchApplicationData",
              applicationId,
            },
          });
          return {
            success: false,
            message:
              "Applicant not found or email doesn't match. Please check your credentials.",
          };
        }

        if (documentsError || !documentsData) {
          console.error("Error fetching documents:", documentsError);
          Sentry.captureException(documentsError, {
            extra: {
              context: "useFetchApplicationData",
              applicationId,
            },
          });
          return {
            success: false,
            message: "Documents not found. Please check your application ID.",
          };
        }

        if (assessmentError || !assessmentData) {
          console.error("Error fetching assessment:", assessmentError);
          Sentry.captureException(assessmentError, {
            extra: {
              context: "useFetchApplicationData",
              applicationId,
            },
          });
          return {
            success: false,
            message: "Assessment not found. Please check your application ID.",
          };
        }

        if (bookingsError) {
          console.error("Error fetching session bookings:", bookingsError);
          Sentry.captureException(bookingsError, {
            extra: {
              context: "useFetchApplicationData",
              applicationId,
            },
          });
          return {
            success: false,
            message:
              "Session bookings not found. Please check your application ID.",
          };
        }

        // Extract DVLA documents
        const licenseFrontDoc = documentsData.find(
          (doc) => doc.type === "dvla-front"
        );
        const licenseBackDoc = documentsData.find(
          (doc) => doc.type === "dvla-back"
        );
        const insuranceDoc = documentsData.find(
          (doc) => doc.type === "insurance-proof"
        );
        const rtwDocumentDoc = documentsData.find(
          (doc) => doc.type === "rtw-document"
        );
        const bankProofDoc = documentsData.find(
          (doc) => doc.type === "bank-proof"
        );
        const headshotDoc = documentsData.find(
          (doc) => doc.type === "headshot"
        );
        const contractDoc = documentsData.find(
          (doc) => doc.type === "contract"
        );

        // Extract CPC, Tacho, and D4 Medical documents
        const cpcFrontDoc = documentsData.find(
          (doc) => doc.type === "cpc-front"
        );
        const cpcBackDoc = documentsData.find((doc) => doc.type === "cpc-back");
        const tachoFrontDoc = documentsData.find(
          (doc) => doc.type === "tacho-card-front"
        );
        const tachoBackDoc = documentsData.find(
          (doc) => doc.type === "tacho-card-back"
        );
        const d4MedicalDoc = documentsData.find(
          (doc) => doc.type === "d4-medical"
        );

        // Separate assessments by type
        const sjTestData = assessmentData.find((a) => a.type === "sjt");
        const orientationData = assessmentData.find(
          (a) => a.type === "orientation-quiz"
        );
        const driverOnroadTestData = assessmentData.find(
          (a) => a.type === "driver-onroad-test"
        );

        // Extract scores
        const situationalScore = sjTestData?.score || null;
        const orientationScore = orientationData?.score || null;
        const driverOnroadTestScore = driverOnroadTestData?.score || null;

        // Separate bookings by session type
        const orientationBooking = sessionBookings?.find(
          (booking) => booking.session?.type === "orientation"
        );

        const vehicleCollectionBooking = sessionBookings?.find(
          (booking) => booking.session?.type === "vehicle_collection"
        );

        // Map database records to Application interface
        const application: Application = {
          id: applicationData.id,
          applicationId: applicationData.application_id,
          airtablePersonId: applicationData.airtable_person_id || undefined,
          jobId: applicationData.job_id || undefined,
          stage: applicationData.current_stage || "basic-details",
          currentStage: applicationData.current_stage || "basic-details",
          currentSegment: applicationData.current_segment || null,
          stageNote: applicationData.stage_note || "",
          referenceDriverId: applicationData.ref_driver_id || undefined,
          contractSalary: applicationData.contract_salary || "",
          basicDetails: {
            firstName: applicantData.first_name || "",
            lastName: applicantData.last_name || "",
            email: applicantData.email || "",
            phoneNumber: applicantData.phone_number || "",
            dateOfBirth: applicantData.date_of_birth || "",
            rightToWork: applicantData.eligibility_rtw,
            validDrivingLicense: applicantData.eligibility_dvla,
            ownElectricVan: applicantData.has_electric_van,
            needsVan: applicantData.needs_hived_van || false,
            workDays: applicantData.availability_days || [],
            workDaysRange: applicantData.availability_days_count || "",
          },
          personalDetails: {
            bankName: applicantData.bank_name || "",
            accountNumber: applicantData.account_number || "",
            sortCode: applicantData.sort_code || "",
            accountName: applicantData.account_holder_name || "",
            proofOfBankDetails: bankProofDoc?.file || "",
            nextOfKin: {
              name: applicantData.next_of_kin_name || "",
              phone: applicantData.next_of_kin_phone || "",
              relation: applicantData.next_of_kin_relation || "",
            },
            utrNumber: applicantData.utr_number || "",
            nationalInsuranceNumber: applicantData.ni_number || "",
            company: {
              type: applicantData.company_type || "",
              number: applicantData.company_number || "",
              vatNumber: applicantData.vat_number || "",
            },
            address: {
              line1: applicantData.address_line_1 || "",
              line2: applicantData.address_line_2 || "",
              city: applicantData.city || "",
              postcode: applicantData.postcode || "",
            },
          },
          //TODO: This may not be needed in this format
          formsCompleted: {
            vehicleReleaseForm: !!contractDoc || false,
            driverContract: applicantData.driver_contract || false,
            onboardingAgreement: applicantData.onboarding_agreement || false,
          },
          assessmentResults: {
            sjtScore: situationalScore || 0,
            driverOnroadTestScore: driverOnroadTestScore || 0,
          },
          documents: {
            drivingLicenceNumber: applicantData.driving_licence_number || "",
            licenseFront: licenseFrontDoc?.file || "",
            licenseBack: licenseBackDoc?.file || "",
            rtwShareCode: applicantData.share_code_rtw || "",
            insuranceProof: insuranceDoc?.file || "",
            passportOrBirthCertificate: rtwDocumentDoc?.file || "",
            insuranceProvider: applicantData.insurance_provider || "",
            policyNumber: applicantData.policy_number || "",
            vehicleReleaseForm: contractDoc?.file || "",
            headshot: headshotDoc?.file || "",
            // CPC
            cpcFront: cpcFrontDoc?.file || "",
            cpcBack: cpcBackDoc?.file || "",
            // Tacho card
            tachoFront: tachoFrontDoc?.file || "",
            tachoBack: tachoBackDoc?.file || "",
            tachoCardNumber: applicantData.tachograph_card_number || "",
            // D4 Medical
            d4MedicalReport: d4MedicalDoc?.file || "",
          },
          orientation: {
            date: orientationBooking?.date,
            location: orientationBooking?.session?.depot
              ? orientationBooking.session.depot.address +
                ", " +
                orientationBooking.session.depot.city
              : undefined,
            quizScore: orientationScore || 0,
          },
          vehicleCollection: {
            date: vehicleCollectionBooking?.date,
            location: vehicleCollectionBooking?.session?.depot
              ? vehicleCollectionBooking.session.depot.address +
                ", " +
                vehicleCollectionBooking.session.depot.city
              : undefined,
          },
          vehicleBooking: applicationData.vehicle_booking || {
            paymentMethodId: "",
            stripeCustomerId: "",
            depositAmount: 0,
            depositPaid: false,
            bookingDate: "",
          },
        };

        return {
          success: true,
          application,
        };
      } catch (error) {
        console.error("Error in fetchApplicationData:", error);
        Sentry.captureException(error, {
          extra: {
            context: "useFetchApplicationData",
            applicationId,
          },
        });
        return {
          success: false,
          message: "Failed to retrieve application data. Please try again.",
        };
      }
    },
    enabled: !!applicationId, // Only run the query if applicationId is provided
  });
};

/**
 * Hook for starting a new application process
 */
export const useStartApplication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      application,
      redirectUrl,
    }: {
      application: Application;
      redirectUrl: string;
    }) => {
      const result = await startApplication(application, redirectUrl);

      // If successful, store the applicationId in local storage
      if (result.success && application.applicationId) {
        localStorage.setItem(
          CURRENT_APPLICATION_ID_KEY,
          application.applicationId
        );

        // Invalidate and refetch the application query
        queryClient.invalidateQueries({
          queryKey: ["application", application.applicationId],
        });
      }

      return result;
    },
  });
};

export const useUpdateApplication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      applicationId,
      data,
    }: ApplicationUpdateData): Promise<{
      success: boolean;
      message?: string;
      application?: Application;
    }> => {
      // Store applicationId in local storage
      localStorage.setItem(CURRENT_APPLICATION_ID_KEY, applicationId);
      if (!applicationId) {
        return {
          success: false,
          message: "Application ID is required",
        };
      }

      try {
        // First, get the current application to update
        const { data: applicationData, error: fetchError } = await supabase
          .from("applications")
          .select("*")
          .eq("application_id", applicationId)
          .single();

        if (fetchError || !applicationData) {
          console.error("Error fetching application to update:", fetchError);
          Sentry.captureException(fetchError, {
            extra: {
              context: "useUpdateApplication",
              applicationId,
            },
          });
          return {
            success: false,
            message: "Application not found. Please check your application ID.",
          };
        }

        // Update application record
        const { error: updateError } = await supabase
          .from("applications")
          .update({
            current_stage: data.currentStage || data.stage,
          })
          .eq("application_id", applicationId);

        if (updateError) {
          console.error("Error updating application:", updateError);
          Sentry.captureException(updateError, {
            extra: {
              context: "useUpdateApplication",
              applicationId,
            },
          });
          return {
            success: false,
            message: "Failed to update application data.",
          };
        }

        // If there's applicant data to update and we have an applicant_id
        if (data.basicDetails || data.personalDetails) {
          // Define a more specific type for applicant data
          type ApplicantUpdateFields = {
            first_name?: string;
            last_name?: string;
            email?: string;
            phone_number?: string;
            date_of_birth?: string;
            eligibility_rtw?: boolean;
            eligibility_dvla?: boolean;
            has_electric_van?: boolean;
            needs_hived_van?: boolean;
            availability_days?: string[];
            availability_days_count?: string;
            bank_name?: string;
            bank_account_number?: string;
            bank_sort_code?: string;
            bank_account_name?: string;
            next_of_kin_name?: string;
            next_of_kin_phone?: string;
            next_of_kin_relation?: string;
            national_insurance_number?: string;
            company_type?: string;
            address_line1?: string;
            address_line2?: string;
            address_city?: string;
            address_postcode?: string;
            vehicle_release_form?: boolean;
            driver_contract?: boolean;
            onboarding_agreement?: boolean;
          };

          const applicantUpdateData: ApplicantUpdateFields = {};

          // Map basic details to database fields
          if (data.basicDetails) {
            if (data.basicDetails.firstName)
              applicantUpdateData.first_name = data.basicDetails.firstName;
            if (data.basicDetails.lastName)
              applicantUpdateData.last_name = data.basicDetails.lastName;
            if (data.basicDetails.email)
              applicantUpdateData.email = data.basicDetails.email;
            if (data.basicDetails.phoneNumber)
              applicantUpdateData.phone_number = data.basicDetails.phoneNumber;
            if (data.basicDetails.dateOfBirth)
              applicantUpdateData.date_of_birth = data.basicDetails.dateOfBirth;
            if (data.basicDetails.rightToWork !== undefined)
              applicantUpdateData.eligibility_rtw =
                data.basicDetails.rightToWork;
            if (data.basicDetails.validDrivingLicense !== undefined)
              applicantUpdateData.eligibility_dvla =
                data.basicDetails.validDrivingLicense;
            if (data.basicDetails.ownElectricVan !== undefined)
              applicantUpdateData.has_electric_van =
                data.basicDetails.ownElectricVan;
            if (data.basicDetails.needsVan !== undefined)
              applicantUpdateData.needs_hived_van = data.basicDetails.needsVan;
            if (data.basicDetails.workDays)
              applicantUpdateData.availability_days =
                data.basicDetails.workDays;
            if (data.basicDetails.workDaysRange)
              applicantUpdateData.availability_days_count =
                data.basicDetails.workDaysRange;
          }

          // Map personal details to database fields
          if (data.personalDetails) {
            if (data.personalDetails.bankName)
              applicantUpdateData.bank_name = data.personalDetails.bankName;
            if (data.personalDetails.accountNumber)
              applicantUpdateData.bank_account_number =
                data.personalDetails.accountNumber;
            if (data.personalDetails.sortCode)
              applicantUpdateData.bank_sort_code =
                data.personalDetails.sortCode;
            if (data.personalDetails.accountName)
              applicantUpdateData.bank_account_name =
                data.personalDetails.accountName;

            if (data.personalDetails.nextOfKin) {
              if (data.personalDetails.nextOfKin.name)
                applicantUpdateData.next_of_kin_name =
                  data.personalDetails.nextOfKin.name;
              if (data.personalDetails.nextOfKin.phone)
                applicantUpdateData.next_of_kin_phone =
                  data.personalDetails.nextOfKin.phone;
              if (data.personalDetails.nextOfKin.relation)
                applicantUpdateData.next_of_kin_relation =
                  data.personalDetails.nextOfKin.relation;
            }

            if (data.personalDetails.nationalInsuranceNumber) {
              applicantUpdateData.national_insurance_number =
                data.personalDetails.nationalInsuranceNumber;
            }

            if (data.personalDetails.company?.type) {
              applicantUpdateData.company_type =
                data.personalDetails.company.type;
            }

            if (data.personalDetails.address) {
              if (data.personalDetails.address.line1)
                applicantUpdateData.address_line1 =
                  data.personalDetails.address.line1;
              if (data.personalDetails.address.line2)
                applicantUpdateData.address_line2 =
                  data.personalDetails.address.line2;
              if (data.personalDetails.address.city)
                applicantUpdateData.address_city =
                  data.personalDetails.address.city;
              if (data.personalDetails.address.postcode)
                applicantUpdateData.address_postcode =
                  data.personalDetails.address.postcode;
            }
          }

          // // Map forms completed data
          // if (data.formsCompleted) {
          //   if (data.formsCompleted.vehicleReleaseForm !== undefined) {
          //     applicantUpdateData.vehicle_release_form =
          //       data.formsCompleted.vehicleReleaseForm;
          //   }
          //   if (data.formsCompleted.driverContract !== undefined) {
          //     applicantUpdateData.driver_contract =
          //       data.formsCompleted.driverContract;
          //   }
          //   if (data.formsCompleted.onboardingAgreement !== undefined) {
          //     applicantUpdateData.onboarding_agreement =
          //       data.formsCompleted.onboardingAgreement;
          //   }
          // }
          // Only update if we have data to update
          if (Object.keys(applicantUpdateData).length > 0) {
            const { error: applicantUpdateError } = await supabase
              .from("applicants")
              .update(applicantUpdateData)
              .eq("id", applicationData.applicant_id);

            if (applicantUpdateError) {
              console.error(
                "Error updating applicant data:",
                applicantUpdateError
              );
              return {
                success: false,
                message: "Failed to update applicant data.",
              };
            }
          }
        }

        // Invalidate the query to refetch the updated data
        queryClient.invalidateQueries({
          queryKey: ["application", applicationId],
        });

        return {
          success: true,
          message: "Application updated successfully",
        };
      } catch (error) {
        console.error("Error in updateApplication:", error);
        Sentry.captureException(error, {
          extra: {
            context: "useUpdateApplication",
            applicationId,
          },
        });
        return {
          success: false,
          message: "Failed to update application data. Please try again.",
        };
      }
    },
    onSuccess: (result, variables) => {
      // Invalidate and refetch the application query after a successful mutation
      queryClient.invalidateQueries({
        queryKey: ["application", variables.applicationId],
      });
    },
  });
};
